import { create } from 'zustand';

export type ToastTone = 'info' | 'success' | 'error' | 'warning';

export interface Toast {
  id: string;
  /** Already-translated text. The store never touches i18n itself. */
  message: string;
  tone: ToastTone;
  /** ms; 0 keeps the toast until it is dismissed. */
  duration: number;
  action?: { label: string; onClick: () => void };
}

export interface ToastOptions {
  tone?: ToastTone;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastState {
  toasts: Toast[];
  show: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION = 4000;
// Errors linger: they usually carry an instruction the user has to act on.
const ERROR_DURATION = 7000;

let counter = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  show: (message, options = {}) => {
    counter += 1;
    const id = `toast-${String(counter)}`;
    const tone = options.tone ?? 'info';
    const toast: Toast = {
      id,
      message,
      tone,
      duration: options.duration ?? (tone === 'error' ? ERROR_DURATION : DEFAULT_DURATION),
      ...(options.action ? { action: options.action } : {})
    };
    set((state) => ({ toasts: [...state.toasts, toast] }));
    return id;
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
  clear: () => {
    set({ toasts: [] });
  }
}));

/**
 * Imperative helper for use outside React (query error handlers, etc.).
 * The caller passes text that is ALREADY translated — this layer must not know
 * about locales, exactly as the transport layer must not know about copy.
 */
export const toast = {
  info: (message: string, options?: ToastOptions) =>
    useToastStore.getState().show(message, { ...options, tone: 'info' }),
  success: (message: string, options?: ToastOptions) =>
    useToastStore.getState().show(message, { ...options, tone: 'success' }),
  error: (message: string, options?: ToastOptions) =>
    useToastStore.getState().show(message, { ...options, tone: 'error' }),
  warning: (message: string, options?: ToastOptions) =>
    useToastStore.getState().show(message, { ...options, tone: 'warning' }),
  dismiss: (id: string) => {
    useToastStore.getState().dismiss(id);
  }
};
